import { useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { ClienteWizardNew, type ContextoEdicionCliente } from './wizard/ClienteWizardNew'
import { Loader2 } from 'lucide-react'
import type { ClienteFormValues } from './wizard/clienteSchema'
import type { ClienteConFacetaContable, Servicio } from '@/types/database'

interface ClienteFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cliente?: ClienteConFacetaContable | null
  /** Edición de un cliente existente. Por defecto, la presencia de `cliente`. */
  esEdicion?: boolean
  /**
   * La ficha del cliente en edición no se pudo cargar. En lugar del wizard se muestra el error con
   * `onReintentar`: sin la ficha, el wizard quedaría en modo alta y el guardado pisaría el cliente.
   */
  errorCarga?: boolean
  onReintentar?: () => void
  servicios?: Servicio[]
  serviciosSeleccionados?: string[]
  /** Ids del catálogo de `etiquetas_cliente` que ya lleva el cliente, en modo edición. */
  etiquetasSeleccionadas?: string[]
  /**
   * Opt-in: el selector de etiquetas solo aparece cuando el caller reconcilia lo elegido
   * después de crear o editar el cliente. El alta rápida desde la búsqueda global
   * (`PageHeader.tsx`) no lo hace, así que sin esta prop en true las etiquetas elegidas ahí
   * se perderían en silencio.
   */
  mostrarEtiquetas?: boolean
  onSubmit: (data: ClienteFormValues, contexto: ContextoEdicionCliente) => void
  /** Valores con los que arranca el alta. Tienen que ser estables entre renders. */
  valoresIniciales?: Partial<ClienteFormValues>
  /** Carga inicial de lo que el wizard necesita: en su lugar se muestra un spinner. */
  isLoading?: boolean
  /**
   * Guardado en curso. El wizard sigue montado con sus valores y su línea base de servicios, con los
   * controles deshabilitados, y el diálogo no se cierra: si el guardado falla, el reintento parte de
   * lo que el usuario cargó.
   */
  isSaving?: boolean
}

export function ClienteFormDialog({
  open,
  onOpenChange,
  cliente,
  esEdicion = !!cliente,
  errorCarga = false,
  onReintentar,
  servicios = [],
  serviciosSeleccionados = [],
  etiquetasSeleccionadas = [],
  mostrarEtiquetas = false,
  onSubmit,
  valoresIniciales,
  isLoading,
  isSaving = false,
}: ClienteFormDialogProps) {
  const [isFormDirty, setIsFormDirty] = useState(false)

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty,
    onDiscard: () => onOpenChange(false),
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && (isSaving || holdClose())) return
    onOpenChange(newOpen)
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
          <SheetHeader className="px-6 pt-6">
            <SheetTitle>
              {esEdicion ? 'Editar Cliente' : 'Nuevo Cliente'}
            </SheetTitle>
            <SheetDescription>
              {esEdicion
                ? 'Modifica los datos del cliente'
                : 'Completa los datos para crear un nuevo cliente'}
            </SheetDescription>
          </SheetHeader>

          {/* Contenido del Wizard */}
          <div className="flex-1 min-h-0 px-6 py-4 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : errorCarga ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
                <p className="text-sm text-destructive">No se pudo cargar el cliente.</p>
                {onReintentar && (
                  <Button type="button" variant="outline" onClick={onReintentar}>
                    Reintentar
                  </Button>
                )}
              </div>
            ) : (
              <fieldset disabled={isSaving} aria-busy={isSaving} className="m-0 h-full min-w-0 border-0 p-0">
                <ClienteWizardNew
                  cliente={cliente}
                  servicios={servicios}
                  serviciosSeleccionados={serviciosSeleccionados}
                  etiquetasSeleccionadas={etiquetasSeleccionadas}
                  mostrarEtiquetas={mostrarEtiquetas}
                  onSubmit={onSubmit}
                  onDirtyChange={setIsFormDirty}
                  isSaving={isSaving}
                  valoresIniciales={valoresIniciales}
                />
              </fieldset>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {confirmation}
    </>
  )
}
